import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";

import type {RevaLanguage} from "@reva/shared/i18n";
import {formatValue} from "@reva/shared/number-format";
import {SPF_PARAMS} from "@reva/shared/parameter-resolution";

import {useLanguage} from "../../hooks/languageHooks";
import {navigateToRouteAndHash} from "../../hooks/navigationHooks";
import {selectAssociatedOutputParamOutputCurried} from "../../redux/objectParametersSelectors";
import {ALL_SOURCES_TO_ROUTE} from "../../redux/objectParametersSlice";
import {
    resetOrEmptySpf,
    selectCanResetSpf,
    selectSpecialPropertyFeaturesOutput,
} from "../../redux/specialPropertyFeaturesThunk";
import type {RevaDispatch} from "../../redux/store";
import ParameterFieldContainer from "./ParameterFieldContainer";

const TK = "object.tab_inspection.input";

interface IndicatedFeature {
    value: number | null;
}

interface SpfOutput {
    special_property_feature_list?: IndicatedFeature[];
    special_property_feature_list_lending?: IndicatedFeature[];
}

interface InputSpecialPropertyFeaturesDisabledProps {
    id: string;
    originTab?: keyof typeof ALL_SOURCES_TO_ROUTE;
}

export default function InputSpecialPropertyFeaturesDisabled({
    id,
    originTab = "inspectionDerived",
}: InputSpecialPropertyFeaturesDisabledProps) {
    const {t} = useTranslation();
    const language = useLanguage();
    const navigate = useNavigate();
    const dispatch = useDispatch<RevaDispatch>();
    const specialPropertyFeaturesList = useSelector(selectSpecialPropertyFeaturesOutput) as SpfOutput | null;
    const canReset = useSelector(selectCanResetSpf);

    const onResetOrEmpty = (value: "empty" | null) => dispatch(resetOrEmptySpf(value == null ? null : []));

    const associatedOutputParamMarketValue = useSelector(selectAssociatedOutputParamOutputCurried(SPF_PARAMS.values)) as
        | number
        | null;

    const placeholder = associatedOutputParamMarketValue
        ? `${t("object.associatedOutputParamPrefix")} ${formatValue(associatedOutputParamMarketValue, language, false)}`
        : undefined;

    const onClick = () =>
        navigateToRouteAndHash(navigate, ALL_SOURCES_TO_ROUTE[originTab], "input_inspection_special_property_features");

    return (
        <ParameterFieldContainer
            id={id}
            label={t(`${TK}.spf_title`)}
            parameter={SPF_PARAMS.values}
            parameterType="INSPECTION_DERIVED"
            showUnit={true}
            showDescription={true}
            noReset={!canReset}
            onResetOrEmpty={onResetOrEmpty}
            hasAssociatedOutputParam={!!associatedOutputParamMarketValue}
            associatedOutputParamValue={associatedOutputParamMarketValue}
            renderControl={({value, allValues}: {value?: unknown; allValues?: Record<string, unknown> | null}) => {
                const market = specialPropertyFeaturesList?.special_property_feature_list;
                const lending = specialPropertyFeaturesList?.special_property_feature_list_lending;
                if (JSON.stringify(market) === JSON.stringify(lending)) {
                    const isEmpty =
                        allValues?.reva === "empty" ||
                        (allValues?.reva == null &&
                            Array.isArray(allValues?.inspectionDerived) &&
                            allValues.inspectionDerived.length === 0);
                    const fromInput = Array.isArray(value)
                        ? value.map((v) => ({value: v as number | null}))
                        : undefined;
                    return (
                        <div className="w-full cursor-pointer p-inputwrapper" onClick={onClick}>
                            <InputText
                                id={id}
                                className="w-full pointer-events-none text-right border-r-0"
                                disabled={true}
                                value={sumLabel(market ?? fromInput, language)}
                                placeholder={isEmpty ? t("object.valueTooltip.revaEmpty") : placeholder}
                                data-pr-position="top"
                            />
                        </div>
                    );
                }
                return (
                    <div className="w-full cursor-pointer p-inputwrapper" onClick={onClick}>
                        <InputTextarea
                            id={id}
                            className="w-full pointer-events-none text-right border-r-0"
                            style={{resize: "none"}}
                            disabled={true}
                            value={`${t(`${TK}.spf_market`)}: ${sumLabel(market, language)}\n${t(`${TK}.spf_lending`)}: ${sumLabel(lending, language)}`}
                            placeholder={placeholder}
                            data-pr-position="top"
                            onClick={onClick}
                        />
                    </div>
                );
            }}
        />
    );
}

/** Summe als Rechenweg, z.B. „1.000 € + 2.000 € - 500 € = 2.500 €“. */
export function sumLabel(features: IndicatedFeature[] | undefined, language: RevaLanguage): string {
    if (!features?.length) return "";
    const values = features.map(({value}) => value).filter((value): value is number => value != null);
    const total = values.reduce((acc, value) => acc + value, 0);
    if (values.length <= 1) return formatValue(total, language, true);

    const parts = values
        .map((value, i) =>
            i === 0
                ? formatValue(value, language, true)
                : value < 0
                  ? ` - ${formatValue(-value, language, true)}`
                  : ` + ${formatValue(value, language, true)}`,
        )
        .join("");
    return `${parts} = ${formatValue(total, language, true)}`;
}
